import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const industries = [
  {
    icon: "ri-hammer-line",
    name: "Home Services",
    desc: "Plumbing, HVAC, roofing, and more—AI that answers, books, and follows up instantly.",
    stat: "+30%",
    statLabel: "Booked Jobs",
  },
  {
    icon: "ri-scales-3-line",
    name: "Legal",
    desc: "Automated intake and qualification so you only spend time on the right clients.",
    stat: "+25%",
    statLabel: "Conversion Rate",
  },
  {
    icon: "ri-hospital-line",
    name: "Medical & Medspa",
    desc: "Smart scheduling and follow-up systems that fill calendars and boost retention.",
    stat: "+40%",
    statLabel: "Appointment Retention",
  },
  {
    icon: "ri-bank-line",
    name: "Financial Services",
    desc: "Automated pipeline management that keeps deal flow consistent without manual effort.",
    stat: "2x",
    statLabel: "Pipeline Throughput",
  },
];

const IndustriesSectionHome = () => {
  const { ref: headRef, visible: headVisible } = useScrollReveal();
  const { ref: imgRef, visible: imgVisible } = useScrollReveal();
  const { ref: listRef, visible: listVisible } = useScrollReveal();

  return (
    <section className="bg-white dark:bg-[#0d0d0d] py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section label + headline */}
        <div
          ref={headRef}
          className={`reveal-el ${headVisible ? 'is-visible' : ''} text-center max-w-3xl mx-auto mb-20`}
        >
          <p className="text-sapphire text-xs font-bold uppercase tracking-widest mb-8">Industries</p>
          <h2 className="text-5xl lg:text-6xl xl:text-7xl font-black text-[#1d1d1f] dark:text-white leading-[1.04] tracking-tight">
            Built for Service-Based<br />Businesses
          </h2>
        </div>

        {/* Split layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Large image */}
          <div
            ref={imgRef}
            className={`reveal-scale ${imgVisible ? 'is-visible' : ''} relative rounded-3xl overflow-hidden h-[560px] lg:sticky lg:top-24`}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/ccb314bfd0b959734a0e193b8598ed87.jpg"
              alt="Service businesses"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1d1d1f]/70 via-transparent to-transparent"></div>
            {/* Floating stat badge */}
            <div className="absolute bottom-8 left-8 right-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
              <p className="text-white font-black text-2xl tracking-tight">Service-Based Industries</p>
              <p className="text-white/60 text-sm mt-1">Where speed to lead directly impacts revenue.</p>
            </div>
          </div>

          {/* Right: Industry cards */}
          <div ref={listRef} className="space-y-4">
            {industries.map((ind, i) => (
              <div
                key={ind.name}
                className={`reveal-el stagger-${i + 1} ${listVisible ? 'is-visible' : ''} group flex items-start gap-6 bg-[#f5f5f7] dark:bg-white/[0.05] hover:bg-[#ebebed] dark:hover:bg-white/[0.09] rounded-3xl p-7 transition-colors duration-300 cursor-default`}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-sapphire/10 group-hover:bg-sapphire transition-colors duration-300 flex-shrink-0">
                  <i className={`${ind.icon} text-sapphire group-hover:text-white text-xl transition-colors duration-300`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-[#1d1d1f] dark:text-white mb-1">{ind.name}</h3>
                  <p className="text-sm text-[#6e6e73] dark:text-white/40 leading-relaxed">{ind.desc}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-2xl font-black text-sapphire tracking-tight">{ind.stat}</div>
                  <div className="text-[10px] text-[#6e6e73] dark:text-white/40 font-medium mt-0.5 whitespace-nowrap">{ind.statLabel}</div>
                </div>
              </div>
            ))}

            <div className={`reveal-el stagger-5 ${listVisible ? 'is-visible' : ''} pt-4`}>
              <Link
                to="/industries"
                className="inline-flex items-center gap-2 text-sapphire text-sm font-semibold hover:gap-3 transition-all cursor-pointer whitespace-nowrap"
              >
                See Industry Solutions
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustriesSectionHome;
